import React from "react";

/**
 * StationLogo — logos monograma únicos por estación.
 * Paleta: negro profundo + lila (#8A6BFF / #5B2EFF / #C4B5FD).
 * Estilo: geométrico, plano, moderno, todos con la misma "familia visual"
 * (disco negro con acento lila) para mantener coherencia.
 *
 * Props:
 *  - id: identificador de estación (baqueira-beret, la-molina…)
 *  - size: diámetro en px (por defecto 44)
 */
export const StationLogo = ({ id, size = 44 }) => {
  const s = size;
  const common = {
    width: s,
    height: s,
    viewBox: "0 0 64 64",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
    "data-testid": `station-logo-${id}`,
  };
  const bg = "#0B0B12";
  const ring = "rgba(138,107,255,0.35)";
  const lilac = "#8A6BFF";
  const lilacHi = "#C4B5FD";
  const lilacDeep = "#5B2EFF";

  const Disc = ({ children }) => (
    <svg {...common}>
      <defs>
        <radialGradient id={`g-${id}`} cx="30%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#1a1230" />
          <stop offset="100%" stopColor={bg} />
        </radialGradient>
      </defs>
      <circle cx="32" cy="32" r="30" fill={`url(#g-${id})`} stroke={ring} strokeWidth="1" />
      {children}
    </svg>
  );

  switch (id) {
    // Baqueira Beret — corona / picos triples (resort premium)
    case "baqueira-beret":
      return (
        <Disc>
          <path
            d="M16 44 L24 24 L30 34 L36 20 L42 34 L48 26 L48 44 Z"
            fill={lilac}
            opacity="0.9"
          />
          <circle cx="36" cy="16" r="2.2" fill={lilacHi} />
          <path d="M16 46 L48 46" stroke={lilacDeep} strokeWidth="2" strokeLinecap="round" />
        </Disc>
      );

    // La Molina — sol sobre montaña (pionera, herencia)
    case "la-molina":
      return (
        <Disc>
          <circle cx="32" cy="24" r="7" fill={lilacHi} opacity="0.9" />
          {[0, 45, 90, 135].map((deg) => (
            <line
              key={deg}
              x1="32" y1="24" x2="32" y2="10"
              stroke={lilac} strokeWidth="1.5" strokeLinecap="round"
              transform={`rotate(${deg} 32 24)`}
              opacity="0.75"
            />
          ))}
          <path d="M12 46 L26 30 L36 40 L46 26 L52 46 Z" fill={lilacDeep} />
        </Disc>
      );

    // Masella — dos pistas paralelas descendiendo (descenso largo)
    case "masella":
      return (
        <Disc>
          <path
            d="M16 50 Q28 30 44 14"
            stroke={lilac} strokeWidth="4" fill="none" strokeLinecap="round"
          />
          <path
            d="M22 50 Q32 34 48 20"
            stroke={lilacHi} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.85"
          />
          <circle cx="44" cy="14" r="3" fill={lilacHi} />
        </Disc>
      );

    // Boí Taüll — el pico más alto: triángulo agudo con línea de cota
    case "boi-taull":
      return (
        <Disc>
          <path d="M32 12 L50 46 L14 46 Z" fill={lilac} />
          <path d="M32 12 L42 30 L32 30 Z" fill={lilacHi} opacity="0.9" />
          <line x1="12" y1="34" x2="52" y2="34" stroke={lilacDeep} strokeWidth="1" strokeDasharray="2 3" opacity="0.9" />
        </Disc>
      );

    // Port del Comte — arco / portal (nombre significa "puerto")
    case "port-del-comte":
      return (
        <Disc>
          <path
            d="M16 46 L16 32 Q16 18 32 18 Q48 18 48 32 L48 46"
            fill="none" stroke={lilac} strokeWidth="4" strokeLinecap="round"
          />
          <circle cx="32" cy="32" r="3.5" fill={lilacHi} />
          <line x1="14" y1="48" x2="50" y2="48" stroke={lilacDeep} strokeWidth="2" strokeLinecap="round" />
        </Disc>
      );

    // Port Ainé — doble pico afilado (pistas empinadas)
    case "port-aine":
      return (
        <Disc>
          <path d="M12 48 L26 20 L34 36 L42 22 L52 48 Z" fill={lilac} />
          <path d="M26 20 L26 48" stroke={lilacDeep} strokeWidth="1" opacity="0.5" />
          <path d="M42 22 L42 48" stroke={lilacDeep} strokeWidth="1" opacity="0.5" />
          <circle cx="26" cy="20" r="2" fill={lilacHi} />
        </Disc>
      );

    // Espot Esquí — líneas de agua/hoja (Aigüestortes NP)
    case "espot-esqui":
      return (
        <Disc>
          <path
            d="M14 40 Q22 32 30 40 Q38 48 46 40 Q50 36 50 30"
            fill="none" stroke={lilac} strokeWidth="3" strokeLinecap="round"
          />
          <path
            d="M14 46 Q22 38 30 46 Q38 54 46 46"
            fill="none" stroke={lilacHi} strokeWidth="2" strokeLinecap="round" opacity="0.7"
          />
          <circle cx="32" cy="20" r="4" fill={lilacDeep} />
          <circle cx="32" cy="20" r="1.5" fill={lilacHi} />
        </Disc>
      );

    // Vallter 2000 — circo glaciar (media luna)
    case "vallter":
      return (
        <Disc>
          <path
            d="M14 42 A18 18 0 0 1 50 42"
            fill="none" stroke={lilac} strokeWidth="4" strokeLinecap="round"
          />
          <path
            d="M20 42 A12 12 0 0 1 44 42"
            fill="none" stroke={lilacHi} strokeWidth="2" strokeLinecap="round" opacity="0.85"
          />
          <line x1="12" y1="48" x2="52" y2="48" stroke={lilacDeep} strokeWidth="2" strokeLinecap="round" />
          <circle cx="32" cy="42" r="2" fill={lilacHi} />
        </Disc>
      );

    // Vall de Núria — santuario: cruz/estrella sobre valle (cremallera)
    case "vall-de-nuria":
      return (
        <Disc>
          <path d="M12 46 L24 36 L32 42 L40 30 L52 46 Z" fill={lilacDeep} opacity="0.9" />
          <g transform="translate(32 20)">
            <path
              d="M0 -8 L2 -2 L8 0 L2 2 L0 8 L-2 2 L-8 0 L-2 -2 Z"
              fill={lilacHi}
            />
          </g>
        </Disc>
      );

    // Fallback genérico
    default:
      return (
        <Disc>
          <path d="M14 46 L26 28 L34 38 L42 24 L50 46 Z" fill={lilac} />
        </Disc>
      );
  }
};

export default StationLogo;
